/**
 * Shine Santé Theme JavaScript
 * Handles animations, interactions, and dynamic behaviors
 */

document.addEventListener('DOMContentLoaded', function() {
  // Initialize all features
  initScrollAnimations();
  initMobileMenu();
  initCartUpdates();
});

/**
 * Scroll Animations using IntersectionObserver
 * Adds 'is-visible' class to elements with data-animate attribute
 */
function initScrollAnimations() {
  // Check if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion) {
    // If reduced motion is preferred, make all animated elements visible immediately
    const animatedElements = document.querySelectorAll('[data-animate], [data-animate-stagger]');
    animatedElements.forEach(element => {
      element.classList.add('is-visible');
    });
    return;
  }

  // Create IntersectionObserver for scroll animations
  const observerOptions = {
    root: null,
    rootMargin: '0px 0px -50px 0px',
    threshold: 0.05
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  // Observe all elements with data-animate attribute
  const animatedElements = document.querySelectorAll('[data-animate], [data-animate-stagger]');
  animatedElements.forEach(element => {
    observer.observe(element);
  });

  // Safety fallback: force all animated elements visible after 800ms
  // Prevents permanent white/invisible sections if IntersectionObserver misfires on mobile
  setTimeout(function() {
    document.querySelectorAll('[data-animate], [data-animate-stagger]').forEach(function(el) {
      el.classList.add('is-visible');
    });
  }, 800);
}

/**
 * Mobile Menu Toggle
 * Handles opening and closing of mobile navigation
 */
function initMobileMenu() {
  const mobileMenuToggle = document.querySelector('[data-mobile-menu-toggle]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  
  if (!mobileMenuToggle || !mobileMenu) return;

  mobileMenuToggle.addEventListener('click', function() {
    const isOpen = mobileMenu.classList.toggle('is-open');
    mobileMenuToggle.setAttribute('aria-expanded', isOpen);
    document.body.style.overflow = isOpen ? 'hidden' : '';
  });

  // Close menu when clicking outside
  document.addEventListener('click', function(event) {
    if (!mobileMenu.contains(event.target) && !mobileMenuToggle.contains(event.target)) {
      mobileMenu.classList.remove('is-open');
      mobileMenuToggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  });

  // Close menu on escape key
  document.addEventListener('keydown', function(event) {
    if (event.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
      mobileMenu.classList.remove('is-open');
      mobileMenuToggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  });
}

/**
 * Cart Updates
 * Updates cart count in header when items are added
 */
function initCartUpdates() {
  // Listen for custom cart update events
  document.addEventListener('cart:updated', function(event) {
    updateCartCount(event.detail.itemCount);
  });
}

/**
 * Update cart count display
 */
function updateCartCount(count) {
  const cartCountElements = document.querySelectorAll('[data-cart-count]');
  cartCountElements.forEach(element => {
    element.textContent = count;
    element.style.display = count > 0 ? 'flex' : 'none';
  });
}

/**
 * Add to Cart Handler
 * Generic function to add products to cart
 */
window.addToCart = async function(variantId, quantity = 1) {
  try {
    const response = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    const data = await response.json();
    
    // Get updated cart
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    
    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {
        itemCount: cart.item_count,
        cart: cart
      }
    }));

    return data;
  } catch (error) {
    console.error('Error adding to cart:', error);
    throw error;
  }
};

/**
 * Card Add to Cart with visual feedback
 * Wraps addToCart with button state changes
 */
window.cardAddToCart = async function(button, variantId, quantity = 1) {
  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = '...';
  try {
    await window.addToCart(variantId, quantity);
    button.textContent = '✓';
    button.style.background = '#16a34a';
    setTimeout(() => {
      button.textContent = originalText;
      button.style.background = '';
      button.disabled = false;
    }, 1500);
  } catch (error) {
    button.textContent = '✗';
    button.style.background = '#dc2626';
    setTimeout(() => {
      button.textContent = originalText;
      button.style.background = '';
      button.disabled = false;
    }, 1500);
  }
};

/**
 * Debounce Helper
 * Limits the rate at which a function can fire
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle Helper
 * Ensures a function is only called at most once in a specified time period
 */
function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}
